import React from 'react';
import { LogOut, RefreshCw, X } from 'lucide-react';
import { TABS, type PortalTab } from './kundenportalSeiteLogic';

/**
 * Seitenrahmen der Kundenseite: grauer Grund, weisse Karte (max-w-3xl),
 * Kopf mit Buero und Betrieb, Reiterleiste (am Handy waagerecht scrollbar).
 * Layout wie TeilnahmeRahmen — dvh und Safe-Area-Polster.
 */
export const PortalRahmen: React.FC<{
  bueroName?: string;
  betriebName?: string;
  tab?: PortalTab;
  onTab?: (t: PortalTab) => void;
  meldung?: string | null;
  onMeldungSchliessen?: () => void;
  onNeuLaden?: () => void;
  onAbmelden?: () => void;
  busy?: boolean;
  children: React.ReactNode;
}> = ({ bueroName, betriebName, tab, onTab, meldung, onMeldungSchliessen, onNeuLaden, onAbmelden, busy = false, children }) => (
  <div
    className="min-h-screen bg-slate-100 flex flex-col items-center p-3 sm:p-4 gap-3"
    style={{
      minHeight: '100dvh',
      paddingTop: 'calc(0.75rem + env(safe-area-inset-top))',
      paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))',
    }}
  >
    <main className="bg-white rounded-2xl shadow-sm border border-slate-200 w-full max-w-3xl text-base text-slate-800 overflow-hidden">
      <header className="px-4 sm:px-5 pt-4 pb-3 border-b border-slate-200 flex items-start justify-between gap-3">
        <div className="min-w-0">
          {bueroName && <p className="text-xs font-medium text-slate-500">{bueroName} · Kundenbereich</p>}
          <h1 className="text-xl font-bold text-slate-900 truncate">{betriebName || 'Kundenbereich'}</h1>
        </div>
        {(onNeuLaden || onAbmelden) && (
          <div className="flex items-center gap-1 shrink-0">
            {onNeuLaden && (
              <button
                type="button"
                onClick={onNeuLaden}
                disabled={busy}
                title="Neu laden"
                aria-label="Neu laden"
                className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 ${busy ? 'animate-spin' : ''}`} />
              </button>
            )}
            {onAbmelden && (
              <button
                type="button"
                onClick={onAbmelden}
                title="Abmelden"
                aria-label="Abmelden"
                className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100"
              >
                <LogOut className="w-5 h-5" />
              </button>
            )}
          </div>
        )}
      </header>

      {tab && onTab && (
        <nav className="border-b border-slate-200 overflow-x-auto" aria-label="Bereiche">
          <div className="flex min-w-max px-2">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => onTab(t.id)}
                aria-current={tab === t.id ? 'page' : undefined}
                className={`min-h-[48px] px-4 text-sm font-bold whitespace-nowrap border-b-2 transition-colors ${
                  tab === t.id ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </nav>
      )}

      {meldung && (
        <div className="mx-4 sm:mx-5 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900 flex items-start gap-2" role="status">
          <span className="flex-1">{meldung}</span>
          {onMeldungSchliessen && (
            <button type="button" onClick={onMeldungSchliessen} aria-label="Meldung schließen" className="p-1 rounded-full hover:bg-amber-100">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      <div className="p-4 sm:p-5 space-y-4">{children}</div>
    </main>
  </div>
);
